import { useMemo, useState, type CSSProperties } from 'react'
import { shortPrice } from '../../utils/format'

/* The price filter: what the floor costs, drawn as a distribution, with a
   handle at each end of the span the visitor wants.

   The histogram is the point of it. A pair of numbers tells you nothing about
   where the stock actually sits; twenty-eight bars tell you at a glance that
   this showroom is mostly saloons around a crore with a couple of Fortuners
   above them — and the two handles then cut that shape rather than an
   abstract line. */

const BARS = 28

/* Two native range inputs stacked on one track, which is the only way to keep
   the platform's keyboard handling and its screen-reader announcements on a
   two-handle control. Everything else here is paint. */
type PriceRangeProps = {
  min: number
  max: number
  from: number
  to: number
  /** Every asking price on the floor — the shape the bars are drawn from. */
  prices: number[]
  onChange: (range: { from: number; to: number }) => void
}

const STEP = 50000

const snapDown = (value: number) => Math.floor(value / STEP) * STEP
const snapUp = (value: number) => Math.ceil(value / STEP) * STEP

function PriceRange({ min, max, from, to, prices, onChange }: PriceRangeProps) {
  /* The track's ends sit on whole steps, so a handle pushed to either end
     lands exactly on the floor's cheapest or dearest car rather than a step
     short of it. */
  const start = snapDown(min)
  const end = Math.max(snapUp(max), start + STEP)

  /* Dragging a range input fires on every step. The URL is written when the
     handle is let go instead — a drag across the track should not leave forty
     entries of work behind it, and the grid should not re-sort under a
     moving thumb. */
  const [draft, setDraft] = useState({ from, to })
  const [committed, setCommitted] = useState({ from, to })
  if (committed.from !== from || committed.to !== to) {
    setCommitted({ from, to })
    setDraft({ from, to })
  }

  const commit = () => {
    if (draft.from !== from || draft.to !== to) onChange(draft)
  }

  const bars = useMemo(() => {
    const span = end - start || 1
    const counts = new Array<number>(BARS).fill(0)

    for (const price of prices) {
      const index = Math.min(
        BARS - 1,
        Math.max(0, Math.floor(((price - start) / span) * BARS)),
      )
      counts[index] += 1
    }

    /* Twenty cars across twenty-eight buckets is mostly empty buckets, which
       reads as specks rather than as a distribution. A short kernel spreads
       each car over its neighbours, so the row draws the shape of the floor's
       pricing the way a density plot does. */
    const kernel = [0.35, 0.75, 1, 0.75, 0.35]
    const smoothed = counts.map((_, index) =>
      kernel.reduce(
        (sum, weight, offset) => sum + weight * (counts[index + offset - 2] ?? 0),
        0,
      ),
    )

    const peak = Math.max(...smoothed, 1)
    return smoothed.map((value, index) => ({
      /* A floor of 8% so an empty stretch of the range is still a row of
         ticks — a gap in the middle of the histogram would read as a broken
         control rather than as no cars at that money. */
      height: Math.max(0.08, value / peak),
      price: start + ((index + 0.5) / BARS) * span,
    }))
  }, [prices, start, end])

  const percent = (value: number) => ((value - start) / (end - start)) * 100

  const fromPercent = percent(draft.from)
  const toPercent = percent(draft.to)

  const label = (value: number) => `PKR ${shortPrice(value)}`

  return (
    <div>
      {/* ---------- the distribution ---------- */}
      <div aria-hidden="true" className="flex h-12 items-end gap-[2px]">
        {bars.map((bar, index) => (
          <span
            key={index}
            style={{ height: `${bar.height * 100}%` }}
            className={`min-w-0 flex-1 rounded-[1px] transition-colors duration-200 ${
              bar.price >= draft.from && bar.price <= draft.to
                ? 'bg-lime/85'
                : 'bg-line'
            }`}
          />
        ))}
      </div>

      {/* ---------- the two handles ---------- */}
      <div className="relative mt-2 h-6">
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-[10px] h-1 rounded-full bg-line"
        />
        <span
          aria-hidden="true"
          className="absolute top-[10px] h-1 rounded-full bg-lime"
          style={{
            left: `${fromPercent}%`,
            width: `${Math.max(toPercent - fromPercent, 0)}%`,
          }}
        />

        <input
          type="range"
          min={start}
          max={end}
          step={STEP}
          value={draft.from}
          aria-label="Minimum price"
          aria-valuetext={label(draft.from)}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              from: Math.min(Number(event.target.value), current.to - STEP),
            }))
          }
          onPointerUp={commit}
          onKeyUp={commit}
          onBlur={commit}
          /* Once both handles are at the top of the track the lower one is
             underneath the upper one and can never be picked up again — so it
             comes to the front as soon as it is up there. */
          style={{ zIndex: fromPercent > 60 ? 4 : 2 } as CSSProperties}
          className="range-dual"
        />

        <input
          type="range"
          min={start}
          max={end}
          step={STEP}
          value={draft.to}
          aria-label="Maximum price"
          aria-valuetext={label(draft.to)}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              to: Math.max(Number(event.target.value), current.from + STEP),
            }))
          }
          onPointerUp={commit}
          onKeyUp={commit}
          onBlur={commit}
          style={{ zIndex: 3 } as CSSProperties}
          className="range-dual"
        />
      </div>

      {/* ---------- what the handles say ---------- */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        {[
          { key: 'from', title: 'From', value: draft.from },
          { key: 'to', title: 'To', value: draft.to },
        ].map((field) => (
          <p
            key={field.key}
            className="flex items-center justify-between gap-2 rounded-lg border border-line-soft bg-surface px-3 py-2.5"
          >
            <span className="label-sm text-dim">{field.title}</span>
            <output className="truncate text-[12.5px] font-semibold text-platinum tabular-nums">
              {shortPrice(field.value)}
            </output>
          </p>
        ))}
      </div>
    </div>
  )
}

export default PriceRange
